"use client";

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";
import { cn } from "@/utils";

// ──────────────────────────────
// Root-level wrappers
// ──────────────────────────────
function Dialog(props: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger(props: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal(props: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogOverlay({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        "fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
        className
      )}
      {...props}
    />
  );
}

// ──────────────────────────────
// Header (optional)
// ──────────────────────────────
function DialogHeader({ className, children, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn(
        "relative flex items-center justify-between bg-[#82b495] text-white rounded-t-md px-4 py-3",
        className
      )}
      {...props}
    >
      <div className="text-lg font-semibold">{children}</div>
      <DialogPrimitive.Close
        data-slot="dialog-close"
        className="absolute right-4 top-4 rounded-xs opacity-80 hover:opacity-100 transition-opacity focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2"
      >
        <XIcon className="size-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </div>
  );
}

// ──────────────────────────────
// Content (flex layout + sticky footer ready)
// ──────────────────────────────
export interface DialogContentProps extends React.ComponentProps<typeof DialogPrimitive.Content> {
  header?: React.ReactNode;
  showOverlay?: boolean;
}

function DialogContent({
  className,
  children,
  header,
  showOverlay = true,
  ...props
}: DialogContentProps) {
  return (
    <DialogPortal>
      {showOverlay && <DialogOverlay />}
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          "fixed top-1/2 left-1/2 z-50 flex min-h-0 max-h-[90vh] w-full max-w-[80vw] translate-x-[-50%] translate-y-[-50%] flex-col rounded-lg border bg-background shadow-lg duration-200 sm:max-w-7xl",
          className
        )}
        {...props}
      >
        {/* Header */}
        {header && <DialogHeader>{header}</DialogHeader>}

        <div className="flex-1 overflow-auto">{children}</div>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

// ──────────────────────────────
// Footer / Title / Description
// ──────────────────────────────
function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end border-t bg-white p-4",
        className
      )}
      {...props}
    />
  );
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("text-lg font-semibold leading-none", className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  );
}

// ──────────────────────────────
// Export
// ──────────────────────────────
export { Dialog, DialogTrigger, DialogContent, DialogFooter, DialogTitle, DialogDescription };
